<script lang="ts">
    import { debounce } from "lodash"
    import { mdiFilter, mdiViewList, mdiViewModule } from "@mdi/js"
    import { getAccount, getOperation } from "../../api"
    import { getOperationTextFilter, requireLogin, tooltip } from "../../helper"
    import { bookmarks, openOverlay, showPrices, login, followUpMap } from "../../store"
    import { _ } from "svelte-i18n"
    import Icon from "../widgets/Icon.svelte"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import Table from "../widgets/Table.svelte"
    import { getFollowUpButton, getOperationButtons } from "./Operations.svelte"
    import OperationsLegend from "./operations/OperationsLegend.svelte"

    import { icons } from "../../config"
    import { getTableColumn } from "../../getTableColumns"
    import { hasOpOutPermission, isAdmin } from "../../../../api/hooks/config-client"
    import { initialLocale } from "../../i18n"
    export let lang: string = "de"

    let search: string = "",
        visibleFollowUpLists: number[] = [],
        availableAccounts: Account[] = [],
        gridview = false,
        innerWidth = window.innerWidth

    const gridBreakPoint = 480
    const additionalColumns: AllTableColumns[] = []
    const getOperationOutTable1Columns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
            ...getTableColumn(_showPrices, "operationOut1Bookmarks", additionalColumns),
        ],
        getOperationOutTable2Columns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
            ...getTableColumn(_showPrices, "operationOut2Bookmarks", additionalColumns),
        ],
        getOperation1TableColumns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
            ...getTableColumn(_showPrices, "operation1Bookmarks", additionalColumns),
        ],
        getOperation2TableColumns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
            ...getTableColumn(_showPrices, "operation2Bookmarks", additionalColumns),
        ]

    type CollectionTable = {
        heading: string
        loading?: boolean
        data?: FollowUpOperation[] | FollowUpOperationOut[]
        collectionName: "operation" | "operation-out"
        type?: number
        getColumns: (_: boolean) => TableColumn[]
        icon: string
        iconViewBox: string
        sort: string
        onSort?: (e: CustomEvent) => Promise<void>
    }

    let allOperationCollectionTables: CollectionTable[] = [
        {
            heading: $_("bookmarks.offers"),
            collectionName: "operation",
            type: 1,
            getColumns: getOperation1TableColumns,
            icon: icons.offerIn,
            iconViewBox: "0 0 214.13 214.13",
            sort: "-number",
        },
        {
            heading: $_("bookmarks.orders"),
            collectionName: "operation",
            type: 2,
            getColumns: getOperation2TableColumns,
            icon: icons.order,
            iconViewBox: "0 0 180.2 203.87",
            sort: "-number",
        },
        {
            heading: $_("bookmarks.outgoingOffers"),
            collectionName: "operation-out",
            getColumns: getOperationOutTable1Columns,
            type: 1,
            icon: icons.offerOut,
            iconViewBox: "0 0 200 200",
            sort: "-number",
        },
        {
            heading: $_("bookmarks.orderConfirmations"),
            collectionName: "operation-out",
            getColumns: getOperationOutTable2Columns,
            type: 2,
            icon: icons.offerOut,
            iconViewBox: "0 0 200 200",
            sort: "-number",
        },
    ]
    requireLogin().then((_login) => {
        visibleFollowUpLists.push(_login.account.ident.personNumber)
        getAccount({
            "ident.dealerNumber": _login.account.ident.dealerNumber,
        }).then((res) => {
            availableAccounts.push(...res.data)
            availableAccounts.sort((a, b) => {
                if (a.ident.personNumber === $login?.account?.ident.personNumber) return -1
                if (b.ident.personNumber === $login?.account?.ident.personNumber) return 1
                return 0
            })
            availableAccounts = availableAccounts
        })
    })

    let loading = false,
        hasResults = false

    async function load(_search: string) {
        hasResults = false
        const personNumbers = availableAccounts.map((a) => a.ident.personNumber)

        for (const table of allOperationCollectionTables) {
            table.onSort = async (sortEvent) => {
                table.data = await handleSort(table, personNumbers, _search)
                allOperationCollectionTables = allOperationCollectionTables
            }
            await table.onSort(null)
        }
    }
    async function handleSort(table: CollectionTable, personNumbers: number[], _search: string) {
        table.data = []
        loading = true

        const volatileDataStorage: FollowUpOperation[] | FollowUpOperationOut[] = []

        for (const pn of personNumbers) {
            if (!shouldProcessPersonNumber(pn, table)) continue
            const filter = createFilter(table, pn, _search)
            if (!filter) continue
            const { data } = await getOperation(table.collectionName, filter, null, null, null, table.sort)
            if (!data?.length) continue
            hasResults = true
            const followUpEntries = collectionEntriesToFollowUpOperationEntries(data, table.collectionName, pn) as any
            volatileDataStorage.push(...followUpEntries)
        }
        loading = false
        return volatileDataStorage
    }

    function shouldProcessPersonNumber(pn: number, table: CollectionTable) {
        return visibleFollowUpLists.includes(pn) && $bookmarks[table.collectionName]?.[pn]
    }

    function createFilter(table: CollectionTable, pn: number, _search: string) {
        const bookmarkIds = Object.keys($bookmarks[table.collectionName][pn])
        if (!bookmarkIds.length) return null
        const filter: any = { _id: { $in: bookmarkIds } }
        if (table.type) filter.type = table.type
        if (_search) filter.$and = [getOperationTextFilter(_search)]
        return filter
    }

    function collectionEntriesToFollowUpOperationEntries(
        operations: Operation[] | OperationOut[],
        collectionName: "operation" | "operation-out",
        pn: number
    ): FollowUpOperation[] | FollowUpOperationOut[] {
        const processedEntries = operations.map((operation) =>
            SingleOperationToFollowUpOperation(operation, collectionName, pn)
        )
        return collectionName === "operation"
            ? (processedEntries as FollowUpOperation[])
            : (processedEntries as FollowUpOperationOut[])
    }

    function SingleOperationToFollowUpOperation(
        operation: Operation | OperationOut,
        collectionName: "operation" | "operation-out",
        pn: number
    ) {
        const rawFollowUpDate = $followUpMap?.[collectionName]?.[pn]?.[operation.id]?.date,
            followUpDate = new Date(rawFollowUpDate)

        return {
            ...operation,
            attachedAccount: availableAccounts.find((a) => a.ident.personNumber === pn),
            followUpDate: isNaN(followUpDate.getTime()) ? undefined : followUpDate,
        }
    }

    const loadDebounced = debounce((_search: string) => load(_search), 1000)

    function sortRowsByFollowUpDates(tableIndex: number, sort: "followUpButton.sort" | "-followUpButton.sort") {
        allOperationCollectionTables[tableIndex].data = allOperationCollectionTables[tableIndex].data.sort((a, b) => {
            const dateA = a.followUpDate ? a.followUpDate.getTime() : 0,
                dateB = b.followUpDate ? b.followUpDate.getTime() : 0
            if (sort == "followUpButton.sort") return dateA - dateB
            else return dateB - dateA
        })
    }

    $: {
        let doLoad = false
        for (const col in $bookmarks) {
            if (
                availableAccounts.some(
                    (a) =>
                        visibleFollowUpLists.includes(a.ident.personNumber) &&
                        Object.keys($bookmarks?.[col as "operation" | "operation-out"]?.[a.ident.personNumber] || {})
                            .length
                )
            ) {
                doLoad = true
                break
            }
        }
        loading = doLoad
        if (doLoad) loadDebounced(search)
        else hasResults = false
    }

    $: hasBookmarksForOtherThanOwnAccount = availableAccounts.some(
        (a) =>
            a.ident.personNumber !== $login?.account?.ident.personNumber &&
            (Object.keys($bookmarks?.operation?.[a.ident.personNumber] || {}).length ||
                Object.keys($bookmarks?.["operation-out"]?.[a.ident.personNumber] || {}).length)
    )

    const hasBookmarksForAccount = (pn: number) =>
        Object.keys($bookmarks?.operation?.[pn] || {}).length ||
        Object.keys($bookmarks?.["operation-out"]?.[pn] || {}).length
    const isAllowedToSeeBookmarksForAccount = (pn: number) =>
        isAdmin($login) ||
        !!Object.keys($bookmarks?.operation?.[pn] || {}).length ||
        (hasOpOutPermission($login) && !!Object.keys($bookmarks?.["operation-out"]?.[pn] || {}).length)
</script>

<style lang="less">
    #followUpSelect {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        margin-bottom: 1.2em;
        margin-top: 1.2rem;
        gap: 1.2rem;
        button {
            background: #ffffff;
            border-radius: 0.25rem;
            border-style: solid;
            border-color: var(--silver);
            color: var(--darkbg);
            border-width: 0.05rem;
            padding: 1.2rem 2.4rem 1.2rem 2.4rem;
            min-width: 320px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            height: 2.5rem;
            position: relative;
            flex-grow: 1;
            &.active {
                background-color: var(--renzrot);
                color: white;
            }
        }
    }
</style>

<svelte:window bind:innerWidth />

<section class="section_padding" data-lang="{lang}">
    <div class="container_wide">
        <h1 class="layout justify-content-space-between align-items-center">
            <span>{$_("bookmarks.bookmarks")}</span>
            <a
                href="/{initialLocale}/operations?type=1"
                class="btn btn-secondary btn-outline"
                on:click|preventDefault="{() => {
                    history.back()
                }}">&laquo; {$_("general.back")}</a
            >
        </h1>
        {#if hasBookmarksForOtherThanOwnAccount}
            <section id="followUpSelect">
                {#each availableAccounts.filter((a) => hasBookmarksForAccount(a.ident.personNumber) && isAllowedToSeeBookmarksForAccount(a.ident.personNumber)) as a}
                    <button
                        class="btn"
                        class:active="{visibleFollowUpLists.includes(a.ident.personNumber)}"
                        on:click="{() => {
                            if (visibleFollowUpLists.includes(a.ident.personNumber))
                                visibleFollowUpLists = visibleFollowUpLists.filter((i) => i !== a.ident.personNumber)
                            else visibleFollowUpLists.push(a.ident.personNumber)
                            visibleFollowUpLists = visibleFollowUpLists
                            loadDebounced(search)
                        }}"
                    >
                        {#if $login?.account?.ident?.personNumber === a.ident.personNumber}
                            {$_("bookmarks.myBookmarks")}
                        {:else}
                            {a.firstname} {a.lastname} ({a.ident.dealerNumber}-{a.ident.personNumber})
                        {/if}
                    </button>
                {/each}
            </section>
        {/if}
        <div class="tabletop bookmark-search">
            <div class="left">
                <div class="searchbar">
                    <input type="text" placeholder="{$_('bookmarks.fullTextSearch')}" bind:value="{search}" />
                    <span class="material-icons icon">search</span>
                </div>
            </div>
            {#if search}
                <div class="right">
                    <!-- Filter Trigger -->
                    <div class="btn filter-button">
                        <span class="icon"><Icon path="{mdiFilter}" size="24px" /></span>
                        <a
                            href="/"
                            data-cy="reset"
                            on:click|preventDefault="{() => {
                                search = ''
                            }}"><span class="text">{$_("bookmarks.resetFilter")}</span></a
                        >
                    </div>
                </div>
            {/if}
        </div>

        {#if hasResults && $login?.account}
            {#each allOperationCollectionTables as d, i (d.collectionName + d.type)}
                {#if d.loading}
                    <MiniLoader />
                {:else if d.data?.length}
                    <h2 class="icon-left">
                        <span class="icon">
                            <Icon size="24px" path="{d.icon}" viewBox="{d.iconViewBox}" />
                        </span>
                        {d.heading}
                    </h2>
                    <div class="tabletop">
                        <div class="left">{$_("operationsOut.clickForMoreInfo")}</div>
                        <div class="right">
                            {#if innerWidth > gridBreakPoint}
                                <button
                                    class="item btn transparent"
                                    class:active="{!gridview}"
                                    on:click="{() => {
                                        gridview = false
                                    }}"
                                    use:tooltip="{{ content: $_('operationsOut.tableView') }}"
                                >
                                    <Icon path="{mdiViewList}" size="28px" />
                                </button>

                                <button
                                    class="item btn transparent"
                                    class:active="{gridview}"
                                    on:click="{() => {
                                        gridview = true
                                    }}"
                                    use:tooltip="{{ content: $_('operationsOut.gridView') }}"
                                >
                                    <Icon path="{mdiViewModule}" size="32px" />
                                </button>
                            {/if}
                        </div>
                    </div>
                    <Table
                        id="{d.collectionName + d.type}"
                        bind:sort="{d.sort}"
                        on:sort="{d.onSort}"
                        deactivateSort="{true}"
                        className="operationtable"
                        gridview="{gridview || innerWidth <= gridBreakPoint}"
                        columns="{d.getColumns($showPrices)}"
                        buttonsColumn="{{
                            label: $_('general.actions'),
                            className: 'rueckfrage mw-120 w-120',
                        }}"
                        followUpColumn="{{
                            label: $_('header.watchlist'),
                            className: 'mw-160 max-w-180 w-160',
                        }}"
                        buttons="{getOperationButtons(d.type, $showPrices, $login.account)}"
                        followUpButton="{{ ...getFollowUpButton(d.type, $showPrices, $login.account), sort: true }}"
                        rows="{d.data}"
                        on:followUpButton-sort="{(e) => {
                            sortRowsByFollowUpDates(i, e.detail)
                        }}"
                        on:click="{(r) => {
                            openOverlay(r.detail)
                        }}"
                    />
                {/if}
            {/each}
        {:else if loading}
            <MiniLoader />
        {:else}
            {$_("integrations.noEntriesFound")}
        {/if}

        <OperationsLegend type="{2}" />
    </div>
</section>
